import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { articles } from '@/mocks/articles';
import type { ArticleSection } from '@/mocks/articles';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://acbuy.monster';

function buildArticleSchema(article: typeof articles[0]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.seoDescription,
    image: article.imageUrl,
    datePublished: '2026-06-01',
    dateModified: '2026-06-24',
    author: {
      '@type': 'Organization',
      name: 'ACBuy Spreadsheet',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'ACBuy Spreadsheet',
      url: siteUrl,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}/article/${article.slug}`,
    },
  };
}

function ArticleContent({ article }: { article: typeof articles[0] }) {
  return (
    <article className="pt-24 pb-16 md:pb-24">
      <JsonLd data={buildArticleSchema(article)} />
      <div className="w-full px-4 md:px-6">
        <div className="max-w-3xl mx-auto">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-500 transition-colors duration-200">Home</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700">{article.category}</span>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-900">{article.title.length > 40 ? article.title.slice(0, 40) + '...' : article.title}</span>
          </nav>

          <header className="mb-10">
            <span className="inline-block px-3 py-1 text-xs font-semibold tracking-wider uppercase rounded-full bg-primary-100 text-primary-700 mb-4">
              {article.category}
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-foreground-500 text-sm">
              Last updated: June 2026 · {article.readTime}
            </p>
          </header>

          <div className="w-full aspect-[21/9] rounded-xl overflow-hidden mb-10 bg-background-100">
            <img
              src={article.imageUrl}
              alt={article.title}
              title={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="prose-custom text-foreground-700 leading-relaxed space-y-6">
            {article.sections.map((section: ArticleSection, idx: number) => {
              if (section.type === 'intro') {
                return (
                  <p key={idx} className="text-lg text-foreground-800 leading-relaxed">
                    {section.content}
                  </p>
                );
              }
              if (section.type === 'heading') {
                return (
                  <h2 key={idx} className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 pt-4">
                    {section.heading}
                  </h2>
                );
              }
              if (section.type === 'paragraph') {
                return (
                  <p key={idx}>{section.content}</p>
                );
              }
              if (section.type === 'list') {
                return (
                  <ul key={idx} className="space-y-2.5">
                    {section.items?.map((item: string, i: number) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="w-5 h-5 flex items-center justify-center bg-accent-100 rounded-full mt-0.5 flex-shrink-0">
                          <i className="ri-check-line text-xs text-accent-600"></i>
                        </span>
                        <span className="text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                );
              }
              if (section.type === 'tips') {
                return (
                  <div key={idx} className="bg-primary-50 border border-primary-200 rounded-xl p-6 my-8">
                    <h3 className="font-heading text-xl font-bold text-foreground-900 mb-3">
                      {section.heading}
                    </h3>
                    <ul className="space-y-2.5">
                      {section.items?.map((item: string, i: number) => (
                        <li key={i} className="flex items-start gap-3">
                          <span className="w-5 h-5 flex items-center justify-center bg-primary-100 rounded-full mt-0.5 flex-shrink-0">
                            <i className="ri-lightbulb-line text-xs text-primary-600"></i>
                          </span>
                          <span className="text-sm">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              }
              return null;
            })}
          </div>

          <div className="mt-12 pt-8 border-t border-background-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors duration-200"
            >
              <i className="ri-arrow-left-line"></i>
              Back to ACBuy Spreadsheet Homepage
            </Link>

            <div className="flex flex-wrap gap-2">
              {articles.filter(a => a.slug !== article.slug).slice(0, 3).map((related) => (
                <Link
                  key={related.slug}
                  to={`/article/${related.slug}`}
                  className="text-xs text-foreground-500 hover:text-primary-500 bg-background-100 hover:bg-background-200 px-3 py-1.5 rounded-lg transition-colors duration-200"
                >
                  {related.title.length > 35 ? related.title.slice(0, 35) + '...' : related.title}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

function NotFoundArticle() {
  return (
    <div className="pt-24 pb-16">
      <div className="w-full px-4 md:px-6">
        <div className="max-w-3xl mx-auto text-center py-20">
          <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-background-100 rounded-full">
            <i className="ri-file-search-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="font-heading text-3xl font-bold text-foreground-900 mb-3">Article Not Found</h1>
          <p className="text-foreground-500 mb-8">The article you are looking for does not exist or has been moved.</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-lg transition-all duration-200 whitespace-nowrap"
          >
            <i className="ri-home-4-line"></i>
            Return to Homepage
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main>
          <NotFoundArticle />
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main>
        <ArticleContent article={article} />
      </main>
      <Footer />
    </>
  );
}